import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Boxes, Wrench, Users, FileText, AlertTriangle, TrendingUp, Car, ArrowRight,
} from "lucide-react";

const StatCard = ({ icon: Icon, label, value, accent, onClick, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className="text-left bg-[#182232] border border-[#2A364F] rounded-xl p-5 hover:bg-[#1E2A3E] hover:border-[#3B82F6]/50 transition-colors group"
  >
    <div className="flex items-center justify-between">
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="w-6 h-6" />
      </div>
      <ArrowRight className="w-4 h-4 text-[#64748B] group-hover:text-[#3B82F6] transition-colors" />
    </div>
    <div className="mt-4 font-heading text-3xl font-extrabold text-[#F8FAFC]">{value}</div>
    <div className="text-sm text-[#94A3B8] mt-1">{label}</div>
  </button>
);

export default function Dashboard() {
  const { user, isFull } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-[#94A3B8]">Chargement…</div>;

  const condLabel = (c) => (c === "neuf" ? "Neuf" : "Occasion");

  return (
    <div className="space-y-6 animate-fade-up" data-testid="dashboard-page">
      <div
        className="rounded-2xl overflow-hidden relative p-6 sm:p-8 bg-cover bg-center border border-[#2A364F]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(10,13,20,0.96), rgba(10,13,20,0.6)), url('https://images.unsplash.com/photo-1570129476815-ba368ac77013?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400')",
        }}
      >
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-white">
          Bonjour, {user.name.split(" ")[0]}
        </h1>
        <p className="text-[#94A3B8] mt-2 max-w-xl">
          Vue d'ensemble de l'atelier — stock, réparations en cours et alertes.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-stock" icon={Boxes} label="Articles en stock" value={stats.stock_total}
          accent="bg-cyan-500/15 text-cyan-400" onClick={() => navigate("/stock")} />
        <StatCard testid="stat-repairs" icon={Wrench} label="Réparations en cours" value={stats.repairs_en_cours}
          accent="bg-blue-500/15 text-blue-400" onClick={() => navigate("/reparations")} />
        {isFull ? (
          <>
            <StatCard testid="stat-clients" icon={Users} label="Clients" value={stats.clients_total}
              accent="bg-purple-500/15 text-purple-400" onClick={() => navigate("/clients")} />
            <StatCard testid="stat-devis" icon={FileText} label="Devis (brouillons)" value={`${stats.devis_total ?? 0}`}
              accent="bg-amber-500/15 text-amber-400" onClick={() => navigate("/devis")} />
          </>
        ) : (
          <>
            <StatCard testid="stat-vehicles" icon={Car} label="Véhicules suivis" value={stats.vehicles_total}
              accent="bg-purple-500/15 text-purple-400" onClick={() => navigate("/reparations")} />
            <StatCard testid="stat-repairs-total" icon={TrendingUp} label="Total réparations" value={stats.repairs_total}
              accent="bg-amber-500/15 text-amber-400" onClick={() => navigate("/reparations")} />
          </>
        )}
      </div>

      <div className="bg-[#182232] border border-[#2A364F] rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-[#2A364F] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#EF4444]" />
            <h3 className="font-heading font-semibold text-[#F8FAFC]">
              Alertes stock bas
              <span className="ml-2 text-xs bg-[#EF4444]/15 text-[#EF4444] px-2 py-0.5 rounded-full">
                {stats.stock_low_count}
              </span>
            </h3>
          </div>
          <button onClick={() => navigate("/stock")} className="text-sm text-[#3B82F6] hover:underline">
            Voir le stock
          </button>
        </div>
        {stats.stock_low_count === 0 ? (
          <div className="p-6 text-sm text-[#64748B]">Aucune alerte — niveaux de stock corrects. ✅</div>
        ) : (
          <div className="divide-y divide-[#2A364F]">
            {stats.stock_low.map((s) => (
              <div key={s.id} data-testid={`low-stock-${s.id}`} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <div className="text-[#F8FAFC] font-medium">{s.designation}</div>
                  <div className="text-xs text-[#64748B] font-mono">
                    {s.type === "pneu" ? "Pneu" : "Pièce"} · {condLabel(s.condition)} · {s.reference || "réf. —"}
                  </div>
                </div>
                <div className="text-right">
                  <span className="font-mono font-bold text-[#EF4444]">{s.quantite}</span>
                  <span className="text-xs text-[#64748B]"> / seuil {s.seuil_alerte}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
